/**
 * Shared types and constants for media library components.
 */

export interface ThumbnailSet {
  w100?: string;
  w300?: string;
  w600?: string;
  w1200?: string;
  w100webp?: string;
  w300webp?: string;
  w600webp?: string;
  w1200webp?: string;
}

export interface MediaAsset {
  id: string;
  fileName: string;
  s3Key: string | null;
  publicUrl: string | null;
  mimeType: string | null;
  sizeBytes: number;
  createdAt: string;
  assetType?: 'image' | 'video' | 'document' | 'embed';
  thumbnails?: ThumbnailSet | null;
  thumbnailStatus?: 'pending' | 'done' | 'unsupported' | 'failed' | null;
  width?: number | null;
  height?: number | null;
  duration?: number | null;
  altText?: string | null;
  title?: string | null;
  description?: string | null;
  tags?: string[] | null;
  embedUrl?: string | null;
  embedProvider?: string | null;
  embedHtml?: string | null;
  embedThumbnailUrl?: string | null;
  blurDataUrl?: string | null;
}

export interface MediaListResponse {
  items: MediaAsset[];
  limit: number;
  offset: number;
}

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'image/svg+xml', 'image/tiff', 'image/bmp', 'image/heic', 'image/heif',
];

export const ALLOWED_VIDEO_TYPES = [
  'video/mp4', 'video/webm', 'video/quicktime',
  'video/x-msvideo', 'video/x-matroska', 'video/x-flv', 'video/x-ms-wmv',
];

export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-7z-compressed',
  'application/x-rar-compressed',
];

export const ALLOWED_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
  ...ALLOWED_DOCUMENT_TYPES,
];

export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024;

export function isEmbed(asset: MediaAsset): boolean {
  return asset.assetType === 'embed';
}

export function isDocument(asset: MediaAsset): boolean {
  return asset.assetType === 'document';
}

export function isVideo(asset: MediaAsset): boolean {
  return asset.assetType === 'video';
}

export function isImage(asset: MediaAsset): boolean {
  return !asset.assetType || asset.assetType === 'image';
}

export type DocumentPreviewType = 'pdf' | 'office' | 'csv' | 'none';

export function getDocumentPreviewType(mimeType: string | null): DocumentPreviewType {
  if (!mimeType) return 'none';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType === 'text/csv') return 'csv';
  if (
    mimeType.includes('wordprocessingml') || mimeType.includes('spreadsheetml') ||
    mimeType.includes('presentationml') || mimeType === 'application/msword' ||
    mimeType === 'application/vnd.ms-excel' || mimeType === 'application/vnd.ms-powerpoint'
  ) return 'office';
  return 'none';
}

export interface AssetBadge { label: string; color: string; }

export function getAssetBadge(asset: MediaAsset): AssetBadge {
  if (asset.assetType === 'embed') {
    const p = asset.embedProvider ?? 'embed';
    const colors: Record<string, string> = {
      youtube: 'bg-red-600', vimeo: 'bg-sky-500', soundcloud: 'bg-orange-500',
      spotify: 'bg-green-600', loom: 'bg-violet-600',
    };
    return { label: p.toUpperCase(), color: colors[p] ?? 'bg-gray-500' };
  }
  if (asset.assetType === 'document') {
    const mt = asset.mimeType ?? '';
    if (mt === 'application/pdf') return { label: 'PDF', color: 'bg-red-500' };
    if (mt.includes('wordprocessingml') || mt === 'application/msword') return { label: 'DOCX', color: 'bg-blue-600' };
    if (mt.includes('spreadsheetml') || mt === 'application/vnd.ms-excel') return { label: 'XLSX', color: 'bg-green-600' };
    if (mt.includes('presentationml') || mt === 'application/vnd.ms-powerpoint') return { label: 'PPTX', color: 'bg-orange-600' };
    if (mt === 'text/csv') return { label: 'CSV', color: 'bg-teal-600' };
    if (mt.includes('zip') || mt.includes('rar') || mt.includes('7z')) return { label: 'ZIP', color: 'bg-yellow-600' };
    return { label: 'DOC', color: 'bg-gray-500' };
  }
  if (asset.assetType === 'video') return { label: 'VIDEO', color: 'bg-purple-600' };
  return { label: 'IMG', color: 'bg-indigo-600' };
}

export function getAssetIcon(asset: MediaAsset): string {
  if (asset.assetType === 'embed') {
    const p = asset.embedProvider ?? '';
    if (p === 'youtube') return '▶';
    if (p === 'vimeo') return '▶';
    if (p === 'soundcloud') return '🎵';
    if (p === 'spotify') return '🎵';
    return '🔗';
  }
  if (asset.assetType === 'document') {
    const mt = asset.mimeType ?? '';
    if (mt === 'application/pdf') return '📕';
    if (mt.includes('wordprocessingml') || mt === 'application/msword') return '📘';
    if (mt.includes('spreadsheetml') || mt === 'application/vnd.ms-excel') return '📗';
    if (mt.includes('presentationml') || mt === 'application/vnd.ms-powerpoint') return '📙';
    if (mt === 'text/csv') return '📊';
    if (mt.includes('zip') || mt.includes('rar') || mt.includes('7z')) return '🗜';
    return '📄';
  }
  if (asset.assetType === 'video') return '🎬';
  return '🖼';
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/** Returns the best thumbnail URL for an asset (for grid view) */
export function thumbUrl(asset: MediaAsset, size: 'w100' | 'w300' = 'w300'): string | null {
  if (asset.assetType === 'embed') return asset.embedThumbnailUrl ?? null;
  if (asset.thumbnails) {
    return (asset.thumbnails as Record<string, string>)[size] ?? null;
  }
  if (asset.assetType === 'image' && asset.publicUrl) return asset.publicUrl;
  return null;
}
